import { z } from "zod";
import { pulseSurveyRowSchema } from "@/features/pulse-surveys/schemas/pulse-survey.schema";

/// Shared run-status enum so the row and detail schemas below stay in sync
/// (the detail schema previously declared `status` as a loose `z.string()`).
export const pulseSurveyRunStatusEnum = z.enum([
  "pending",
  "open",
  "closed",
  "cancelled",
  "failed",
]);

/// Mirrors shore's `PulseSurveyRunRowDto` in
/// `pulsewise-shore/src/modules/pulse-surveys/run-management/run-management.dto.ts`.
export const pulseSurveyRunRowSchema = z.object({
  id: z.string(),
  status: pulseSurveyRunStatusEnum,
  triggeredAt: z.string(),
  pendingUntil: z.string().nullable(),
  firedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  triggeringEventName: z.string().nullable(),
  recipientCount: z.number(),
  respondedCount: z.number(),
});
export type PulseSurveyRunRow = z.infer<typeof pulseSurveyRunRowSchema>;

export const listRunsResponseSchema = z.object({
  rows: z.array(pulseSurveyRunRowSchema),
  nextCursor: z.string().nullable(),
});
export type ListRunsResponse = z.infer<typeof listRunsResponseSchema>;

export const cancelRunResponseSchema = z.object({
  id: z.string(),
  status: z.literal("cancelled"),
});
export type CancelRunResponse = z.infer<typeof cancelRunResponseSchema>;

/// Per-run detail (questions + AI reasoning), fetched lazily when an
/// AI Pulse run row expands. `questionsSnapshot` is JSON; consumers narrow.
export const pulseSurveyRunDetailSchema = z.object({
  id: z.string(),
  status: pulseSurveyRunStatusEnum,
  triggeredAt: z.string(),
  firedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  triggeringEventName: z.string().nullable(),
  questionsSnapshot: z.unknown(),
  aiGenReasoning: z.string().nullable(),
  aiGenModelVersion: z.string().nullable(),
  recipientCount: z.number().int(),
  respondedCount: z.number().int(),
});
export type PulseSurveyRunDetail = z.infer<typeof pulseSurveyRunDetailSchema>;

/// AI Pulse overview (spec 3.5): the single system survey row, or null when
/// the workspace has no AI Pulse yet. Importing the survey-row schema here is
/// the safe direction (run-schema -> survey-schema); the reverse would cycle.
export const aiPulseOverviewSchema = z.object({
  survey: pulseSurveyRowSchema.nullable(),
});
export type AiPulseOverview = z.infer<typeof aiPulseOverviewSchema>;
